/** @jsxImportSource @emotion/core */
import { css } from '@emotion/core';
import React, { FC, ReactNode } from 'react';
import {
	MdContentCopy,
	MdEmail,
	MdLanguage,
	MdPerson,
	MdPhone,
} from 'react-icons/md';
import ReactGA from 'react-ga';
import ReactGA4 from 'react-ga4';
import { FaFacebookSquare, FaInstagram, FaLinkedin } from 'react-icons/fa';

import { Flex, Box } from 'components/styled';
import Text from 'components/styled/Text';
import { HrefLink } from 'components/styled/Link';
import Avatar from 'components/styled/Avatar';
import { ResponsivePadder } from 'components/styled/Wrapper';
import Button from 'components/styled/Button';
import ModalDialog from 'components/modal';
import Paper from 'components/styled/Paper';
import Divider from 'components/styled/Divider';

import { getFullName } from 'utils';

import CompanyContact from './CompanyContact';
import SocialLink from './SocialLink';

import { CompanyDto, Person, PersonContactDto } from 'typing/endpoints';

const Link: FC<{
	icon: ReactNode;
	href: string;
	children: string;
}> = ({ icon, href, children }) => (
	<Flex alignItems="center" justifyContent={['center', 'flex-start']}>
		<HrefLink href={href} target="_blank" rel="noopener noreferrer">
			<Box display="inline-block" color="primary" mx={2} my={[2, 0]}>
				{icon}
			</Box>
			<Text display={['none', 'inline-block']} mr={2}>
				{children}
			</Text>
		</HrefLink>

		<Box
			display={['inline']}
			p={2}
			title="Skopírovat do schránky"
			onClick={() => navigator.clipboard.writeText(children)}
			css={css`
				cursor: pointer;
			`}
		>
			<MdContentCopy />
		</Box>
	</Flex>
);

type SocialIconsProps = Pick<
	CompanyDto,
	'name' | 'facebookUrl' | 'website' | 'instagramUrl' | 'linkedinUrl'
>;

const linkIcons = [FaFacebookSquare, MdLanguage, FaInstagram, FaLinkedin];
const linkLabels = ['Facebook', 'Website', 'Instagram', 'LinkedIn'];

const SocialIconLink: FC<SocialIconsProps> = ({
	name,
	facebookUrl,
	website,
	instagramUrl,
	linkedinUrl,
}) => {
	const links = [facebookUrl, website, instagramUrl, linkedinUrl]
		.map((link, index) => ({
			url: link,
			icon: linkIcons[index],
			label: linkLabels[index],
		}))
		.filter(l => l.url);
	return (
		<Flex
			justifyContent="space-between"
			flexWrap="wrap"
			maxWidth="100px"
			width={1}
		>
			{links.map(({ url, icon, label }, index) => (
				<Box
					key={index}
					bg="secondary"
					m={2}
					css={css`
						box-shadow: 0px 0px 1px 3px rgb(0 0 0 / 11%);
						border: 1px solid black;
						border-radius: 15%;
					`}
				>
					<SocialLink icon={icon} label={`${label} - ${name}`} href={url} />
				</Box>
			))}
		</Flex>
	);
};

type Props = {
	recruiter: Omit<PersonContactDto, 'photo'> &
		Partial<Pick<Person, 'email'>> & { photo?: string };
	companyInfo: Pick<
		CompanyDto,
		| 'id'
		| 'name'
		| 'headquartersAddress'
		| 'facebookUrl'
		| 'website'
		| 'instagramUrl'
		| 'linkedinUrl'
	>;
	bottom?: boolean;
} & { scrolled?: boolean };

const CompanyContactDialog: FC<Props> = ({
	recruiter,
	companyInfo,
	scrolled,
}) => {
	return (
		<ModalDialog
			label="Dialog kontaktu zaměstnavatele"
			control={openModal => (
				<Button
					height={scrolled ? 50 : ['60px', '80px']}
					minWidth={['200px', '200px']}
					maxWidth={['200px', '200px']}
					onClick={() => {
						openModal();

						ReactGA4.event({
							category: 'company_profile',
							action: 'contact_reveal_click',
							label: `${companyInfo.name} | ID=${companyInfo.id}`,
							transport: 'xhr', // optional, beacon/xhr/image
						});

						ReactGA.event({
							category: 'company_profile',
							action: 'contact_reveal_click',
							label: `${companyInfo.name} | ID=${companyInfo.id}`,
						});
					}}
					css={css`
						box-shadow: 1px 1px 2px 2px rgba(0, 0, 0, 0.3);
					`}
				>
					<Text mr={2}>Zobrazit kontakt</Text>
					<MdPerson size={24} color="white" />
				</Button>
			)}
		>
			{closeModal => (
				<Paper>
					<Flex flexDirection="column">
						<Flex
							width={1}
							flexDirection="column"
							py={4}
							mb={2}
							justifyContent="center"
							alignItems="center"
						>
							<Text
								mt={0}
								mb={3}
								fontSize="xl"
								fontWeight="bold"
								textAlign="center"
							>
								Kontakt zaměstnavatele
							</Text>

							<Flex alignItems="center">
								<Flex alignItems="center" my={2}>
									<Avatar photo={recruiter.photo} size={80} />
									<Box
										mx={3}
										maxWidth={190}
										css={css`
											text-overflow: ellipsis;
											overflow: hidden;
										`}
									>
										<Text fontWeight="bold">{getFullName(recruiter)}</Text>
										<Text>{recruiter.jobTitle}</Text>
									</Box>
								</Flex>
								<Flex flexDirection="column" ml={2} color="text">
									<Link
										icon={<MdEmail />}
										href={`mailto:${recruiter.contactEmail ?? recruiter.email}`}
									>
										{recruiter.contactEmail ?? recruiter.email}
									</Link>
									<Link icon={<MdPhone />} href={`tel:${recruiter.phone}`}>
										{recruiter.phone}
									</Link>
								</Flex>
							</Flex>
						</Flex>
						<Divider size={1} borderColor="primary" mx={3} />
						<Flex flexDirection={['column', 'row']}>
							{(companyInfo.facebookUrl ||
								companyInfo.instagramUrl ||
								companyInfo.linkedinUrl ||
								companyInfo.website) && (
								<ResponsivePadder py={3} textAlign="center" alignItems="center">
									<Text
										mb={3}
										fontSize="xl"
										fontWeight="bold"
										textAlign="center"
									>
										Sociální sítě
									</Text>
									<SocialIconLink {...companyInfo} />
								</ResponsivePadder>
							)}
							<Divider size={1} borderColor="primary" mx={3} />
							<ResponsivePadder py={3} textAlign="center">
								<Text mb={3} fontSize="xl" fontWeight="bold" textAlign="center">
									Sídlo firmy
								</Text>
								<CompanyContact
									name={companyInfo.name}
									{...companyInfo.headquartersAddress}
								/>
							</ResponsivePadder>
						</Flex>
					</Flex>
					<Text
						onClick={closeModal}
						mx={3}
						css={css`
							cursor: pointer;
							&:hover {
								text-decoration: underline;
							}
						`}
					>
						{'<'} Zpět
					</Text>
				</Paper>
			)}
		</ModalDialog>
	);
};

export default CompanyContactDialog;
